// ============================================================================
// Webhooks — track the inbound webhook endpoints The Hive exposes and watch
// what's actually hitting them (live from the durable webhook_events log).
// This is the "don't break the webhooks" cockpit for cutover + monitoring.
// ============================================================================
import { api } from '../lib/api.js'
import { esc } from '../lib/view.js'

function ago(ts) {
  if (!ts) return 'never'
  const s = Math.max(0, Math.floor((Date.now() - Number(ts)) / 1000))
  if (s < 60) return s + 's ago'
  if (s < 3600) return Math.floor(s / 60) + 'm ago'
  if (s < 86400) return Math.floor(s / 3600) + 'h ago'
  return Math.floor(s / 86400) + 'd ago'
}
function etDT(ts) {
  if (!ts) return '—'
  return new Date(Number(ts)).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' })
}

export function renderWebhooks(el) {
  el.innerHTML = `
    <div class="card pad" style="margin-bottom:16px">
      <div class="toolbar">
        <div class="grow">
          <div class="mod-h">Webhooks</div>
          <div class="mod-sub">The inbound endpoints providers call, and a live feed of what's hitting them. These must stay reachable at cutover.</div>
        </div>
        <span class="pill" id="wh-token"></span>
        <button class="btn ghost" id="wh-refresh">Refresh</button>
      </div>
      <div id="wh-endpoints" class="grid" style="grid-template-columns:1fr;gap:12px;margin-top:12px"><div class="empty">Loading…</div></div>
    </div>
    <div class="card pad">
      <div class="toolbar">
        <div class="grow">
          <div class="mod-h">Recent webhook events <span class="pill an" style="vertical-align:middle" id="wh-total"></span></div>
          <div class="mod-sub" style="margin:0">Every call is logged raw first (replayable), then processed.</div>
        </div>
        <select class="h-input" id="wh-size" style="width:auto">
          <option value="25">25 / page</option><option value="50">50 / page</option><option value="100">100 / page</option>
        </select>
        <button class="btn ghost" id="wh-export">Export CSV</button>
      </div>
      <div class="table-wrap" style="margin-top:10px">
        <table class="h-table">
          <thead><tr><th>When (ET)</th><th>Provider</th><th>Kind</th><th>Processed</th><th>Note</th></tr></thead>
          <tbody id="wh-events"><tr><td colspan="5" class="empty">Loading…</td></tr></tbody>
        </table>
      </div>
      <div class="toolbar" style="margin-top:12px;margin-bottom:0">
        <span class="mod-sub" id="wh-count" style="margin:0"></span>
        <span class="grow"></span>
        <button class="btn ghost" id="wh-prev">‹ Prev</button>
        <span class="mod-sub" id="wh-page" style="margin:0"></span>
        <button class="btn ghost" id="wh-next">Next ›</button>
      </div>
    </div>`

  const $ = (id) => el.querySelector('#' + id)
  let pageSize = 25, page = 0, total = 0

  const eventRow = (e) => `
    <tr>
      <td style="white-space:nowrap">${esc(etDT(e.ts))}</td>
      <td>${esc(e.provider || '—')}</td>
      <td><span class="pill ${e.kind === 'inbound' ? 'an' : 'mk'}">${esc(e.kind || '—')}</span></td>
      <td>${e.processed ? '<span class="pill an">✓ done</span>' : '<span class="pill">pending</span>'}</td>
      <td style="max-width:320px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(e.note || '')}</td>
    </tr>`

  function load() {
    api.get(`/api/webhooks?limit=${pageSize}&offset=${page * pageSize}`).then((d) => {
      total = d.eventsTotal || 0
      $('wh-token').textContent = d.tokenSet ? `Token ${esc(d.tokenMask)}` : 'Token not set'
      $('wh-token').className = 'pill ' + (d.tokenSet ? 'an' : 'bad')
      $('wh-total').textContent = total.toLocaleString() + ' total'

      $('wh-endpoints').innerHTML = (d.endpoints || []).map((e) => `
        <div style="border:1px solid var(--line);border-radius:var(--r);padding:14px 16px;background:var(--surface-2)">
          <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
            <span style="font-weight:800">${esc(e.name)}</span>
            <span class="pill">${esc(e.provider)}</span>
            <span class="grow"></span>
            ${e.kind ? `<span class="mod-sub" style="margin:0">${e.last24 || 0} in 24h · ${e.total || 0} total · last ${esc(ago(e.lastAt))}</span>` : '<span class="mod-sub" style="margin:0">cron</span>'}
          </div>
          <div class="mod-sub" style="margin:6px 0 8px">${esc(e.purpose)}</div>
          <code style="display:block;background:var(--surface);border:1px solid var(--line-soft);border-radius:var(--r-sm);padding:8px 10px;font-size:12.5px;word-break:break-all">${esc(e.url)}</code>
        </div>`).join('') || '<div class="empty">No endpoints.</div>'

      const ev = d.events || []
      $('wh-events').innerHTML = ev.length ? ev.map(eventRow).join('')
        : `<tr><td colspan="5" class="empty">No webhook events yet${d.note ? ' — ' + esc(d.note) : ''}.</td></tr>`

      const from = total === 0 ? 0 : page * pageSize + 1
      const to = page * pageSize + ev.length
      const pages = Math.max(Math.ceil(total / pageSize), 1)
      $('wh-count').textContent = `Showing ${from}–${to} of ${total}`
      $('wh-page').textContent = `Page ${page + 1} of ${pages}`
      $('wh-prev').disabled = page <= 0; $('wh-prev').style.opacity = page <= 0 ? '.5' : '1'
      $('wh-next').disabled = page >= pages - 1; $('wh-next').style.opacity = page >= pages - 1 ? '.5' : '1'
    }).catch((e) => { $('wh-endpoints').innerHTML = `<div class="msg bad">${esc(e.message)}</div>` })
  }

  $('wh-export').addEventListener('click', async () => {
    const btn = $('wh-export'), label = btn.textContent; btn.textContent = 'Exporting…'; btn.disabled = true
    try {
      const d = await api.get('/api/webhooks?limit=100000')
      const cols = ['ts', 'provider', 'kind', 'processed', 'note']
      const cell = (e, c) => {
        let v = c === 'ts' ? (e.ts ? new Date(Number(e.ts)).toISOString() : '') : (e[c] == null ? '' : String(e[c]))
        if (/^[=+\-@\t\r]/.test(v)) v = "'" + v; return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v
      }
      const csv = [cols.join(','), ...(d.events || []).map((e) => cols.map((c) => cell(e, c)).join(','))].join('\n')
      const a = document.createElement('a')
      a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
      a.download = `hive-webhook-events-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href)
    } catch (e) { alert('Export failed: ' + e.message) }
    finally { btn.textContent = label; btn.disabled = false }
  })

  $('wh-size').addEventListener('change', () => { pageSize = parseInt($('wh-size').value) || 25; page = 0; load() })
  $('wh-prev').addEventListener('click', () => { if (page > 0) { page--; load() } })
  $('wh-next').addEventListener('click', () => { page++; load() })
  $('wh-refresh').addEventListener('click', () => { page = 0; load() })
  load()
}
